import { uil_delete_vertices,
  uil_get_vertices_of_edge,
  uil_is_object_fixed,
  uil_fix_objects,
  uil_unfix_objects,
  uil_get_face_geometry,
  uil_listify_edges,
  uil_move_vertex_to_coords,
} from './ui_logic';
import { LOG, display_message_to_user } from '@phi/logging/frontend';
import * as THREE from 'three';
import { show_modal, hide_modal } from './modals';
import * as v3 from './vector3';
import { uid_unhighlight_all_objects } from './colours_materials_appearance';
import { WrappedValue } from '@phi/wrapped-value';
import { push_nodups, suppress_undo_tracking, suppress_animate, unsuppress_animate_if_ready } from './utility';
import { uid_deselect_all_objects, uid_select_object } from './ui_display';
import { withSpinner } from './state/spinner';

function assemble_objects_for_make_circular(
  circle_face_ids,
  circle_edge_ids,
  wv_circle_edges_are_open_list,
  circle_arc_endpoint_vertices,
  circle_vertices_on_edges,
  wv_circle_center_vertex,
  exact_circle_fixed_vertices) {

  if ( uid_selected_faces.length > 0) {
    // We collect the edges around the selected faces and disregard any selected edges.
    circle_face_ids.push(... uid_selected_faces.keys());
    if (uid_selected_faces.length > 1) {
      var boundary_edge_ids = new Array();
      var boundary_vertex_ids = new Array();
      var wv_is_one_cc = new WrappedValue();
      uil_get_face_connected_component_info(circle_face_ids, wv_is_one_cc, boundary_edge_ids, boundary_vertex_ids);
      if (!wv_is_one_cc.v) {
        display_message_to_user(LOG.WARN, "Selected faces are disconnected.");
        return false;
      }
      circle_edge_ids.push(... boundary_edge_ids);
    }
    else {
      circle_edge_ids.push(... uil_get_edges_of_face(circle_face_ids[0]));
    }
    wv_circle_edges_are_open_list.v = false;
  }
  else if (uid_selected_edges.length > 0) {
    var res = uil_listify_edges(uid_selected_edges.keys(), wv_circle_edges_are_open_list, circle_edge_ids);
    if (!res) {
      display_message_to_user(LOG.WARN, "Edges are not connected.");
      return false;
    }

    // Find arc endpoint vertices
    if (wv_circle_edges_are_open_list.v) {
      var e1 = circle_edge_ids[0];
      var e2 = circle_edge_ids[circle_edge_ids.length-1];
      var e1v1 = uil_get_vertices_of_edge(e1)[0];
      var e1v2 = uil_get_vertices_of_edge(e1)[1];
      var e2v1 = uil_get_vertices_of_edge(e2)[0];
      var e2v2 = uil_get_vertices_of_edge(e2)[1];
      circle_arc_endpoint_vertices[0] = uil_find_edge_shared_vertex(circle_edge_ids[0], circle_edge_ids[1]) == e1v1 ? e1v2 : e1v1;
      circle_arc_endpoint_vertices[1] = uil_find_edge_shared_vertex(circle_edge_ids[circle_edge_ids.length-2],
        circle_edge_ids[circle_edge_ids.length-1]) == e2v1 ? e2v2 : e2v1;
    }

    else if (circle_edge_ids.length < 3 && !wv_circle_edges_are_open_list.v) {
      display_message_to_user(LOG.WARN, "You must select at least 3 edges to turn into a circle.");
      return false;
    }
  }

  // We have lists of edges, now see what the vertex situation is.
  var selected_vertices_off_edges = [];
  uid_selected_vertices.keys().forEach(function(vid){
    if (vertex_is_on_edges(vid, circle_edge_ids))
      circle_vertices_on_edges.push(vid);
    else
      selected_vertices_off_edges.push(vid);
  });

  // Check also for fixed vertices. If these lie on the edges we keep them
  circle_edge_ids.forEach(function(eid){
    if (uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0])) {
      push_nodups(uil_get_vertices_of_edge(eid)[0], global_exact_circle_fixed_vertices);
      uid_select_object(uil_get_vertices_of_edge(eid)[0]);
      push_nodups(uil_get_vertices_of_edge(eid)[0], exact_circle_fixed_vertices);
      push_nodups(uil_get_vertices_of_edge(eid)[0], circle_vertices_on_edges);
    }
    if (uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1])) {
      push_nodups(uil_get_vertices_of_edge(eid)[1], global_exact_circle_fixed_vertices);
      uid_select_object(uil_get_vertices_of_edge(eid)[1])
      push_nodups(uil_get_vertices_of_edge(eid)[1], exact_circle_fixed_vertices);
      push_nodups(uil_get_vertices_of_edge(eid)[1], circle_vertices_on_edges);
    }
  });

  // Remove arc endpoints from selected vertices on edges
  if (wv_circle_edges_are_open_list.v) {
    for (var i = circle_vertices_on_edges.length-1; i >= 0; i--) {
      if (circle_vertices_on_edges[i] == circle_arc_endpoint_vertices[0] || circle_vertices_on_edges[i] == circle_arc_endpoint_vertices[1])
        circle_vertices_on_edges.splice(i, 1);
    }

    // If it's an arc with only 2 edges and no vertices on them, add their common vertex to the list of vertices on edges
    if (circle_edge_ids.length == 2 && circle_vertices_on_edges.length == 0) {
      let v = uil_find_edge_shared_vertex(circle_edge_ids[0], circle_edge_ids[1]);
      circle_vertices_on_edges.push(v);
    }
  }

  if (selected_vertices_off_edges.length == 1)
    // Note here that a single selected vertex will replace the single vertex found within selected faces, if any.
    wv_circle_center_vertex.v = selected_vertices_off_edges[0];

  return true;
}


export async function uid_make_circular_selected_edges_or_face(do_exact) {
  // Reset variables.
  global_exact_circle_center_vertex_id = 0;
  global_exact_circle_edge_ids = [];
  global_exact_circle_center_coordinates = null;
  global_exact_circle_radius = null;
  global_exact_circle_normal = null;
  global_exact_circle_vertices = [];
  global_exact_circle_fixed_vertices = [];
  global_exact_circle_is_arc = false;
  global_exact_circle_single_edge_vertex_to_remove = 0;

  if (uid_selected_edges.length == 1) {
    // Add a vertex at the middle of the edge and try again for arc
    suppress_animate();
    suppress_undo_tracking();
    suppress_ui_messages();
    var eid = uid_selected_edges.firstKey();
    uid_deselect_all_objects();
    var vid = uil_add_vertex_at_param(eid, 0.5);
    global_exact_circle_single_edge_vertex_to_remove = 0;
    if (vid) {
      var neweids = uil_get_edges_of_vertex(vid);
      uid_select_object(neweids[0]);
      uid_select_object(neweids[1]);
      await uid_make_circular_selected_edges_or_face(do_exact);

      if (!do_exact) {
        uil_delete_vertices([vid], true);
      }
      else {
        global_exact_circle_single_edge_vertex_to_remove = vid;
      }
    }
    uid_deselect_all_objects();
    unsuppress_animate_if_ready();
    unsuppress_undo_tracking_if_ready(false);
    unsuppress_ui_messages_if_ready();
    return;
  }

  suppress_animate();

  var circle_face_ids = [];
  var circle_edge_ids = [];
  var wv_circle_edges_are_open_list = new WrappedValue();
  var circle_arc_endpoint_vertices = [];
  var wv_circle_center_vertex = new WrappedValue(0);
  var circle_vertices_on_edges = [];
  var res = assemble_objects_for_make_circular(
    circle_face_ids,
    circle_edge_ids,
    wv_circle_edges_are_open_list,
    circle_arc_endpoint_vertices,
    circle_vertices_on_edges,
    wv_circle_center_vertex,
    global_exact_circle_fixed_vertices);
  if (!res) {
    uid_deselect_all_objects();
    wrap_up_making_circular();
    return;
  }

  // First we do a quick check to see if there are any fixed edges/vertices/faces in or related to the selected group, and if so we abort.
  var fixed_objects_found = false;
  uid_selected_edges.keys().forEach(function(eid) {
    if (uil_is_object_fixed(eid))
      fixed_objects_found = true;
    if (uil_is_object_fixed(uil_get_vertices_of_edge(eid)[0]) &&
        !global_exact_circle_fixed_vertices.includes(uil_get_vertices_of_edge(eid)[0]))
      fixed_objects_found = true;
    if (uil_is_object_fixed(uil_get_vertices_of_edge(eid)[1]) &&
        !global_exact_circle_fixed_vertices.includes(uil_get_vertices_of_edge(eid)[1]))
      fixed_objects_found = true
  });
  uid_selected_faces.keys().forEach(function(fid){
    if (uil_is_object_fixed(fid))
      fixed_objects_found = true;
    vertices_of_face(fid).forEach(function(vid){
      if (uil_is_object_fixed(vid) && !global_exact_circle_fixed_vertices.includes(vid))
        fixed_objects_found = true;
    });
    uil_get_edges_of_face(fid).forEach(function(eid) {
      if (uil_is_object_fixed(eid))
        fixed_objects_found = true;
    });
  });
  uid_selected_vertices.keys().forEach(function(vid){
    if (uil_is_object_fixed(vid) && !global_exact_circle_fixed_vertices.includes(vid))
      fixed_objects_found = true;
  });

  if (fixed_objects_found) {
    display_message_to_user(LOG.WARN, "Unable to make circular due to fixed objects");
    wrap_up_making_circular();
    return;
  }

  var circle_edges_are_open_list = wv_circle_edges_are_open_list.v;
  var circle_center_vertex = wv_circle_center_vertex.v ? wv_circle_center_vertex.v : 0;

  var preliminary_center = null;
  var preliminary_normal = null;
  var preliminary_radius = 0;

  var final_center = null;
  var final_normal = null;
  var final_radius = 0;

  // Work on center
  if (circle_center_vertex) {
    // User has selected one vertex off of the list of edges, and indicated that this is his center. So:
    final_center = new THREE.Vector3();
    final_center.copy(uid_vertices.getItem(circle_center_vertex).object3d.position);
  }
  // User hasn't selected any vertex off of the list of edges, so we need to choose ourselves. So:
  // (a) User has selected exactly 3 vertices on a closed edge list, or 1 vertex on an open edge loop. This is the
  //     the strongest indication of where they want the center to lie, so we will use it. This is also a strong
  //     indication of what the plane normal and radius should be.
  if (circle_edges_are_open_list && circle_vertices_on_edges.length == 1) {
    var tmp_center = new THREE.Vector3();
    final_normal = new THREE.Vector3();
    var wv_radius = new WrappedValue();
    get_circle_from_three_points(uid_vertices.getItem(circle_vertices_on_edges[0]).object3d.position,
      uid_vertices.getItem(circle_arc_endpoint_vertices[0]).object3d.position,
      uid_vertices.getItem(circle_arc_endpoint_vertices[1]).object3d.position,
      tmp_center, final_normal, wv_radius);
    if (!final_center) {
      final_center = new THREE.Vector3(tmp_center.x, tmp_center.y, tmp_center.z);
    }
    final_radius = wv_radius.v;
  }
  else if (!circle_edges_are_open_list && circle_vertices_on_edges.length == 3) {
    var tmp_center = new THREE.Vector3();
    final_normal = new THREE.Vector3();
    var wv_radius = new WrappedValue();
    get_circle_from_three_points(uid_vertices.getItem(circle_vertices_on_edges[0]).object3d.position,
      uid_vertices.getItem(circle_vertices_on_edges[1]).object3d.position,
      uid_vertices.getItem(circle_vertices_on_edges[2]).object3d.position,
      tmp_center, final_normal, wv_radius);
    if (!final_center) {
      final_center = new THREE.Vector3(tmp_center.x, tmp_center.y, tmp_center.z);
    }
    final_radius = wv_radius.v;
  }
  else if (!circle_edges_are_open_list) {
    // At this point we have a closed list of edges, no faces to give us a center, not enough vertices to give us a center, so we'll use
    // the geometry of the surrounding edges.
    // Get a sorted list of vertices on the edges
    var sorted_vertices = sort_edge_list_vertices(circle_edge_ids);

    // Fill some 'cleverly selected' vertices in the list to use.
    circle_vertices_on_edges = [];
    if (sorted_vertices.length < 5) {
      circle_vertices_on_edges.push(sorted_vertices[0]);
      circle_vertices_on_edges.push(sorted_vertices[1]);
      circle_vertices_on_edges.push(sorted_vertices[2]);
    }
    else {
      circle_vertices_on_edges.push(sorted_vertices[0]);
      circle_vertices_on_edges.push(sorted_vertices[2]);
      circle_vertices_on_edges.push(sorted_vertices[4]);
    }

    var tmp_center = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    var tmp_normal = new THREE.Vector3();
    //uil_get_edge_list_geometry(circle_edge_ids, false, tmp_center, tmp_normal, wv_radius, characteristic_dir_coords);
    uil_get_edge_list_geometry(circle_edge_ids, true, tmp_center, tmp_normal, wv_radius, characteristic_dir_coords);
    if (!final_center) {
      final_center = new THREE.Vector3(tmp_center.x, tmp_center.y, tmp_center.z);
    }
    preliminary_radius = wv_radius.v;
    preliminary_normal = new THREE.Vector3(tmp_normal.x, tmp_normal.y, tmp_normal.z);
  }
  else if (circle_edges_are_open_list) {
    // At this point we have an open list of edges, no faces to give us a center, not enough vertices to give us a center, so we'll just
    // grab 1 random vertex on the list of edges, plus the arc endpoints.
    // Get a sorted list of vertices on the edges
    var sorted_vertices = sort_edge_list_vertices(circle_edge_ids);
    // Remove first and last vertex, which are the arc endpoints
    sorted_vertices.splice(0,1);
    sorted_vertices.splice(sorted_vertices.length-1,1);

    if (sorted_vertices.length == 0) {
      // This is the special case of the single edge to be made circular
      // XXX!!!!
      console_log("MUST CREATE CIRCLE FROM SINGLE EDGE!");
      display_message_to_user(LOG.WARN, "You need to select at least two connected edges to turn them into a circular arc.");
      wrap_up_making_circular();
      return;
    }
    else if (sorted_vertices.length < 3) {
      var tmp_center = new THREE.Vector3();
      preliminary_normal = new THREE.Vector3();
      var wv_radius = new WrappedValue();

      circle_vertices_on_edges = [];
      circle_vertices_on_edges.push(sorted_vertices[0]);

      get_circle_from_three_points(uid_vertices.getItem(circle_vertices_on_edges[0]).object3d.position,
        uid_vertices.getItem(circle_arc_endpoint_vertices[0]).object3d.position,
        uid_vertices.getItem(circle_arc_endpoint_vertices[1]).object3d.position,
        tmp_center, preliminary_normal, wv_radius);
      if (!final_center) {
        final_center = new THREE.Vector3(tmp_center.x, tmp_center.y, tmp_center.z);
      }
      preliminary_radius = wv_radius.v;
    }
    else {
      tmp_center = new THREE.Vector3();
      preliminary_normal = new THREE.Vector3();
      wv_radius = new WrappedValue();

      circle_vertices_on_edges = [];
      circle_vertices_on_edges.push(sorted_vertices[1]);

      get_circle_from_three_points(uid_vertices.getItem(circle_vertices_on_edges[0]).object3d.position,
        uid_vertices.getItem(circle_arc_endpoint_vertices[0]).object3d.position,
        uid_vertices.getItem(circle_arc_endpoint_vertices[1]).object3d.position,
        tmp_center, preliminary_normal, wv_radius);
      if (!final_center) {
        final_center = new THREE.Vector3(tmp_center.x, tmp_center.y, tmp_center.z);
      }
      preliminary_radius = wv_radius.v;
    }
  }
  else {
    // Shit, can't do anything!
    console_log_xml('UNABLE TO GET CENTER!');
    wrap_up_making_circular();
    return;
  }

  console_log('After Center:');
  console_log('Preliminary_center:', preliminary_center);
  console_log('Preliminary_normal:', preliminary_normal);
  console_log('Preliminary_radius:', preliminary_radius);
  console_log('Final_center:', final_center);
  console_log('Final_normal:', final_normal);
  console_log('Final_radius:', final_radius);

  // Now work on normal
  if (final_normal) {
    // We already have picked up a final normal with confidence, so we're done
  }
  else if (circle_face_ids.length == 1) {
    // as a next guess, if we have a single face, use the normal of that face at its center.
    var center = new THREE.Vector3();
    var wv_radius = new WrappedValue(0);
    var wv_flatness = new WrappedValue(0);
    var characteristic_dir_coords = new THREE.Vector3();
    final_normal = new THREE.Vector3();
    uil_get_face_geometry(circle_face_ids[0], center, final_normal, wv_radius, characteristic_dir_coords, wv_flatness);
  }
  else if (preliminary_normal) {
    // We have a reasonable guess, so we'll use this
    final_normal = new THREE.Vector3();
    final_normal.copy(preliminary_normal);
  }
  else {
    console_log('FAILED TO FIND PLANE NORMAL');
    wrap_up_making_circular();
    return;
  }

  console_log('After Normal:');
  console_log('Preliminary_center:', preliminary_center);
  console_log('Preliminary_normal:', preliminary_normal);
  console_log('Preliminary_radius:', preliminary_radius);
  console_log('Final_center:', final_center);
  console_log('Final_normal:', final_normal);
  console_log('Final_radius:', final_radius);

  // Finally work on radius
  if (final_radius > 0) {
    // We have a final radius, we're good
  }
  else if (preliminary_radius > 0) {
    final_radius = preliminary_radius;
  }
  else if (final_center) {
    // We have a center, we'll try to get a radius with a vertex on the edges
    if (circle_edges_are_open_list) {
      final_radius = v3.point_point_distance(final_center, uid_vertices.getItem(circle_arc_endpoint_vertices[0]).object3d.position)
    }
    else if (circle_vertices_on_edges.length > 0) {
      final_radius = v3.point_point_distance(final_center, uid_vertices.getItem(circle_vertices_on_edges[0]).object3d.position)
    }
    else {
      final_radius = v3.point_point_distance(final_center, uid_vertices.getItem(sorted_vertices[0]).object3d.position)
    }
  }

  /*
  console_log('After Radius:');
  console_log('Preliminary_center:', preliminary_center);
  console_log('Preliminary_normal:', preliminary_normal);
  console_log('Preliminary_radius:', preliminary_radius);
  console_log('Final_center:', final_center);
  console_log('Final_normal:', final_normal);
  console_log('Final_radius:', final_radius);
  */

  global_exact_circle_is_arc = circle_edges_are_open_list;
  global_exact_circle_center_vertex_id = circle_center_vertex ? circle_center_vertex : 0;
  global_exact_circle_edge_ids = [... circle_edge_ids];
  global_exact_circle_center_coordinates = new THREE.Vector3();
  global_exact_circle_center_coordinates.copy(final_center);
  global_exact_circle_radius = final_radius;
  global_exact_circle_normal = new THREE.Vector3();
  global_exact_circle_normal.copy(final_normal);
  global_exact_circle_vertices = [];
  global_exact_circle_vertices.push(... circle_vertices_on_edges);

  if (do_exact) {
    unsuppress_animate_if_ready();
    request_global_exact_circle_details_from_user(false);
  }
  else {
    await finish_making_circular_selected_edges_or_face();
    unsuppress_animate_if_ready();
  }
}

function request_global_exact_circle_details_from_user() {
  // USE XXX XXX: global_exact_circle_center, global_exact_circle_normal, global_exact_circle_radius
  show_modal('drawExactCircle');
}

export const finish_making_circular_selected_edges_or_face = withSpinner('Making circular', async function finish_making_circular_selected_edges_or_face(do_exact, cx = 0, cy = 0, cz = 0, d = 0, theta = 0, phi = 0) {
  if (do_exact) {
    hide_modal('drawExactCircle');

    const eps = 0.0005;
    let r = d/2.0;
    if (r < eps || r > global_exact_circle_radius * 10) {
      display_message_to_user(LOG.WARN, "Radius too small, setting to reasonable value");
      r = global_exact_circle_radius;
    }

    global_exact_circle_center_coordinates.set(cx, cy, cz);
    global_exact_circle_radius = Number(r);

    const n = v3.fromAngles(theta * Math.PI/180, phi * Math.PI/180);
    global_exact_circle_normal.copy(n);
  }

  // Then project all of the circle vertices onto the circle plane
  var sorted_vertices = sort_edge_list_vertices(global_exact_circle_edge_ids);

  suppress_undo_tracking();
  suppress_animate();

  // First position the center vertex object, if we have one
  if (typeof global_exact_circle_center_vertex_id != 'undefined' && global_exact_circle_center_vertex_id > 0) {
    uil_move_vertex_to_coords( global_exact_circle_center_vertex_id, global_exact_circle_center_coordinates.x, global_exact_circle_center_coordinates.y, global_exact_circle_center_coordinates.z );
  }

  var vp = new THREE.Vector3();
  sorted_vertices.forEach(function(vid) {
    vp = v3.project_point_on_plane( uid_vertices.getItem(vid).object3d.position, global_exact_circle_center_coordinates, global_exact_circle_normal);
    uil_move_vertex_to_coords( vid, vp.x, vp.y, vp.z );
  });

  // Then move all the vertices radially so they are at a distance equal to the radius from the center. We could use the
  // core function function uil_set_vertex_distance(v1id, v2id, dist) which moved the second vertex only, if we have a vertex at
  // the center, but we don't always have that so we'll just do the geometric calculation for all vertices.
  var vc = new THREE.Vector3();
  sorted_vertices.forEach(function(vid) {
    vp.copy(uid_vertices.getItem(vid).object3d.position);
    var dir = new THREE.Vector3();
    dir.copy(vp);
    dir.sub(global_exact_circle_center_coordinates).normalize();
    dir.multiplyScalar(global_exact_circle_radius);
    vc.addVectors(global_exact_circle_center_coordinates, dir);
    uil_move_vertex_to_coords( vid, vc.x, vc.y, vc.z );
  });

  // Finally call the core to make the circle. Must first append the arc endpoints, if any, to the list of
  // selected vertices on the edges
  var res = uil_can_make_edges_circular(global_exact_circle_edge_ids, global_exact_circle_vertices, global_exact_circle_center_vertex_id);
  if (res) {
    uil_unfix_objects(global_exact_circle_fixed_vertices);
    res = uil_make_edges_circular(global_exact_circle_edge_ids, global_exact_circle_vertices, global_exact_circle_center_vertex_id);
    uil_fix_objects(global_exact_circle_fixed_vertices);
  }
  else
    display_message_to_user(LOG.WARN, "Unable to make circle");

  // And now we should be done
  if (global_exact_circle_single_edge_vertex_to_remove != 0)  {
    uil_delete_vertices([global_exact_circle_single_edge_vertex_to_remove], true);
  }

  unsuppress_undo_tracking_if_ready(false);
  wrap_up_making_circular();
  return;
});

function wrap_up_making_circular() {
  uid_deselect_all_objects();
  uid_unhighlight_all_objects();
  unsuppress_animate_if_ready();
}

/* OBSOLETE
export function make_edges_circular_as_connected_arcs() {
  // At this point we have already placed all of the vertices where they should be, and we need to
  // deform the edges connecting them to form a circle. We'll do this step by step by building
  // arcs between them.

  // Created a sorted list of vertices, adding to the end the first and second vertices again if the edge list forms a loop (i.e. an
  // entire circle)
  var sorted_vertex_list = [];
  if (!global_exact_circle_is_arc) {
    for (var i=0; i < global_exact_circle_edge_ids.length; i++) {
      if (i < global_exact_circle_edge_ids.length-1)
      sorted_vertex_list.push(uil_find_edge_shared_vertex(global_exact_circle_edge_ids[i], global_exact_circle_edge_ids[i+1]));
      else
      sorted_vertex_list.push(uil_find_edge_shared_vertex(global_exact_circle_edge_ids[i], global_exact_circle_edge_ids[0]));
    }

    if (!global_exact_circle_is_arc) {
      sorted_vertex_list.push(sorted_vertex_list[0]);
      sorted_vertex_list.push(sorted_vertex_list[1]);
    }
  }
  else {
    for (var i=0; i <= global_exact_circle_edge_ids.length-2; i++) {
      if (i == 0) {
        var vs = uil_get_vertices_of_edge(global_exact_circle_edge_ids[i]);
        var vid_shared = uil_find_edge_shared_vertex(global_exact_circle_edge_ids[i], global_exact_circle_edge_ids[i+1]);
        if (vid_shared == vs[0])
          sorted_vertex_list.push(vs[1]);
        else
          sorted_vertex_list.push(vs[0]);
      }

      sorted_vertex_list.push(uil_find_edge_shared_vertex(global_exact_circle_edge_ids[i], global_exact_circle_edge_ids[i+1]));

      if (i == global_exact_circle_edge_ids.length-2) {
        var vs = uil_get_vertices_of_edge(global_exact_circle_edge_ids[i+1]);
        var vid_shared = uil_find_edge_shared_vertex(global_exact_circle_edge_ids[i], global_exact_circle_edge_ids[i+1]);
        if (vid_shared == vs[0])
          sorted_vertex_list.push(vs[1]);
        else
          sorted_vertex_list.push(vs[0]);
      }
    }

  }

  var array_of_edges_that_have_been_handled = [];

  // Loop through the vertex list, grabbing 2 consecutive edges between 3 consecutive vertices at a time, to form  short
  // arcs all around
  for (var i = 0; i <= sorted_vertex_list.length - 3; i++) {
    var v1 = sorted_vertex_list[i];
    var v2 = sorted_vertex_list[i+1];
    var v3 = sorted_vertex_list[i+2];
    var e1 = find_edge_from_two_vertices(v1, v2);
    var e2 = find_edge_from_two_vertices(v2, v3);
    var res = uil_make_edges_circular([e1, e2], [v2], 0);
    if (array_of_edges_that_have_been_handled.indexOf(e1) == -1)
      array_of_edges_that_have_been_handled.push(e1);
    if (array_of_edges_that_have_been_handled.indexOf(e2)==-1)
      array_of_edges_that_have_been_handled.push(e2);
    console_log('Array of edges at this step: ', array_of_edges_that_have_been_handled);
  }
  array_of_edges_that_have_been_handled = [];
}
*/
